// apps/codeframe/src/chrome/timeline/TimelineTransport.tsx
import { useEffect, useState } from 'react';
import { Play, Pause, Rewind, Circle } from 'lucide-react';
import { useCanvasStore } from '../../store/canvas-store';
import { getPlayer } from '../../motion/registry';
import { useTimelineStore } from './timeline-store';
import { driveLottie } from './lottieTransport';
import type { MotionTimeline, PlaybackConfig } from '../../store/types';

const MODES: NonNullable<PlaybackConfig['autoplay']>['mode'][] = ['once', 'loop', 'pingpong'];

export function TimelineTransport({ frameId, timeline }: { frameId: string; timeline: MotionTimeline }) {
  const dispatch = useCanvasStore((s) => s.dispatch);
  const recording = useTimelineStore((s) => s.recording);
  const setRecording = useTimelineStore((s) => s.setRecording);
  const [t, setT] = useState(0);

  // poll the live player's time so the readout tracks playback (lightweight)
  useEffect(() => {
    const id = setInterval(() => {
      const p = getPlayer(frameId);
      if (p) setT(p.time);
    }, 60);
    return () => clearInterval(id);
  }, [frameId]);

  const player = getPlayer(frameId);
  const mode = timeline.playback.autoplay?.mode ?? 'loop';

  const setMode = (m: (typeof MODES)[number]) => {
    dispatch({
      t: 'motion.setPlayback',
      frameId,
      playback: { ...timeline.playback, autoplay: { ...timeline.playback.autoplay, mode: m } },
    });
  };

  const setDuration = (ms: number) => {
    if (!Number.isFinite(ms) || ms <= 0) return;
    dispatch({ t: 'motion.setDuration', frameId, duration: Math.round(ms) });
  };

  return (
    <div className="flex items-center gap-3 px-3 py-1.5 border-b border-[var(--color-chip-border)] font-[family-name:var(--font-mono)] text-xs">
      <button onClick={() => { player?.seek(0); driveLottie(frameId, 'seek', 0); }} className="hover:text-[var(--color-accent)]" aria-label="Rewind" title="Rewind">
        <Rewind size={14} />
      </button>
      <button onClick={() => { player?.play(); driveLottie(frameId, 'play'); }} className="hover:text-[var(--color-accent)]" aria-label="Play" title="Play">
        <Play size={14} />
      </button>
      <button onClick={() => { player?.pause(); driveLottie(frameId, 'pause'); }} className="hover:text-[var(--color-accent)]" aria-label="Pause" title="Pause">
        <Pause size={14} />
      </button>
      <button
        onClick={() => setRecording(!recording)}
        aria-label="Record keyframes"
        title="Record: drag elements on the canvas to keyframe x/y at the playhead (R)"
        className={recording ? 'text-[var(--color-danger)]' : 'hover:text-[var(--color-accent)]'}
      >
        <Circle size={14} fill={recording ? 'currentColor' : 'none'} />
      </button>

      <span className="tabular-nums text-[var(--color-text-muted)]">
        {(t / 1000).toFixed(2)}s / {(timeline.duration / 1000).toFixed(2)}s
      </span>

      <label className="ml-auto flex items-center gap-1 text-[var(--color-text-muted)]">
        mode
        <select
          value={mode}
          onChange={(e) => setMode(e.target.value as (typeof MODES)[number])}
          className="cf-select rounded border border-[var(--color-chip-border)] bg-[var(--color-surface)] px-1 py-0.5 text-[var(--color-text)]"
        >
          {MODES.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </label>

      <label className="flex items-center gap-1 text-[var(--color-text-muted)]">
        dur(ms)
        <input
          type="number"
          min={1}
          defaultValue={timeline.duration}
          key={timeline.duration}
          onBlur={(e) => setDuration(Number(e.target.value))}
          className="w-20 rounded border border-[var(--color-chip-border)] bg-[var(--color-surface)] px-1 py-0.5 tabular-nums text-[var(--color-text)]"
        />
      </label>
    </div>
  );
}
